import React, { useEffect, useState } from 'react'
import { useDashboardContext } from '../../../../contexts/NewDashboardContext';
import { alertV2 } from '../../../../api';
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  Card,
  CardBody,
  CardExpandToggler,
} from "../../../../components/card/card";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const AlarmResolutionSpeed = ({}) => {
  const { dateRangeOverview } = useDashboardContext();
  const [chartData, setChartData] = useState(null);
  const navigate = useNavigate();

  // Get theme colors from CSS variables
  const getThemeColors = () => {
    const bodyBg = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-bg")
      .trim();
    const textColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-color")
      .trim();
    const gridColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-border-color")
      .trim();
    const themeColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme")
      .trim();

    return { bodyBg, textColor, gridColor, themeColor };
  };

  const { bodyBg, textColor, gridColor, themeColor } = getThemeColors();

  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const alarmId = chartData.labels[elementIndex];
      navigate(`/alarm/detail/${alarmId}`);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeOutBounce",
    },
    onClick: handleChartClick,
    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Resolution time for Closed Alarms",
        color: textColor,
        font: {
          size: 14,
          weight: "bold",
        },
      },

      tooltip: {
        backgroundColor: bodyBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            return (
              context.parsed.y + (context.parsed.y === 1 ? " day" : " days")
            );
          },
          afterLabel: function (context) {
            return "Click to view alarm details";
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
        },
        grid: {
          color: "#4b4b4b",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Days",
          color: textColor,
          font: {
            size: 12,
          },
        },
        ticks: {
          color: textColor,
          font: {
            size: 12,
          },
          stepSize: 1,
        },
        grid: {
          color: "#4b4b4b",
          drawBorder: false,
        },
      },
    },
  };

  async function getData() {
    try {
      const result = await alertV2.listByDate({
        start_date: dateRangeOverview?.startDate,
        end_date: dateRangeOverview?.endDate,
        status: "Closed",
      });
      const body = await result.json();

      // Process the data to create chart data
      processChartData(body.data);
    } catch (error) {
      console.error("Error fetching alarm data:", error);
      // Use mock data for demonstration
      createMockChartData();
    }
  }

  const processChartData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      console.log("No valid API data, using mock data");
      createMockChartData();
      return;
    }

    console.log("API data:", apiData);
    // Filter only closed alarms and calculate resolution time
    const closedAlarmsWithResolution = [];

    apiData.forEach((alert) => {
      // Only process closed alarms that have both max_date and updated_at
      if (
        alert.max_date &&
        alert.updated_at &&
        alert.alertlogs &&
        alert.alertlogs.length > 0
      ) {
        const alertlog = alert.alertlogs[0];
        const maxDate = new Date(alert.max_date);
        const updatedDate = new Date(alertlog.event_date);

        // Calculate difference in days
        const timeDifference = updatedDate.getTime() - maxDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

        // Only include if resolution time is positive (updated_at is after max_date)
        if (daysDifference > 0) {
          closedAlarmsWithResolution.push({
            id: alert.id,
            resolutionDays: daysDifference,
          });
        }
      }
    });

    // If no closed alarms with valid resolution time, use mock data
    if (closedAlarmsWithResolution.length === 0) {
      console.log(
        "No closed alarms with resolution time found, using mock data"
      );
      createMockChartData();
      return;
    }

    // Sort by resolution time and take top 10 for better visualization
    const sortedAlarms = closedAlarmsWithResolution
      .sort((a, b) => b.resolutionDays - a.resolutionDays)
      .slice(0, 10);

    const labels = sortedAlarms.map((alarm) => alarm.id);
    const data = sortedAlarms.map((alarm) => alarm.resolutionDays);

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: "Resolution Time (Days)",
          data: data,
          backgroundColor: themeColor,
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };

    setChartData(chartData);
  };

  const createMockChartData = () => {
    const data = {
      labels: [],
      datasets: [
        {
          label: "Resolution Time (Days)",
          data: [],
          backgroundColor: themeColor,
          borderWidth: 2,
          borderRadius: 4,
        },
      ],
    };
    setChartData(data);
  };

  useEffect(() => {
    getData();
  }, [dateRangeOverview]);

  // If no data is available yet, show loading
  if (!chartData) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bodyBg,
          borderRadius: "8px",
          border: `1px solid ${gridColor}`,
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardBody>
          <div className="d-flex justify-content-end">
            <CardExpandToggler />
          </div>
          <div
            style={{ position: "relative", minHeight: "370px", width: "100%" }}
          >
            <Bar options={options} data={chartData} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
